import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useProfileContext } from "./context/ProfileContext";
import Spinner from "../app/shared/Spinner";
import { IS_ALGODELTAFX_DOMAIN } from "../Util/constant";

const Dashboard = lazy(() => import("./user-pages/dashboard/Dashboard"));
const AccountManagement = lazy(() => import("./user-pages/account-management/Accounts"));
const AccountInfo = lazy(() => import("./user-pages/account-management/AccountInfo"));
const AccountSymbolInfo = lazy(() => import("./user-pages/account-management/AccountSymbolInfo"));
const Group = lazy(() => import("./user-pages/groups/Groups"));
const GroupInfo = lazy(() => import("./user-pages/groups/GroupInfo"));
const GroupPositions = lazy(() => import("./user-pages/groups/GroupPositions"));
const GroupPendingOrders = lazy(() => import("./user-pages/groups/GroupPendingOrders"));
const GroupPendingOrderDetails = lazy(() => import("./user-pages/groups/GroupPendingOrderUserDetails"));
const Bridge = lazy(() => import("./user-pages/bridge/Bridge"));
const BridgeInfo = lazy(() => import("./user-pages/bridge/BridgeInfo"));
const TradingViewBridgeSyntaxGenerator = lazy(() => import("./user-pages/bridge/TradingViewBridgeSyntaxGenerator"))
const Wallet = lazy(() => import("./user-pages/wallet/Wallet"));
const OrderHistory = lazy(() => import("./user-pages/order-history/OrderHistory"));
const GroupOrderHistoryDetails = lazy(() => import("./user-pages/order-history/GroupOrderUserDetails"));
const Referral = lazy(() => import("./user-pages/referral/Referral"));
const ActivityLogs = lazy(() => import("./user-pages/activity-logs/ActivityLogs"));
const Profile = lazy(() => import("./user-pages/profile/Profile"));
const Configs = lazy(() => import("./user-pages/configs/Configs"));
const GroupStrategy = lazy(() => import("./user-pages/group-strategy/GroupStrategy"));
const GroupStrategyDetails = lazy(() => import("./user-pages/group-strategy/GroupStrategyDetails"));
const UserManagement = lazy(() => import("./user-pages/user-management/UserManagement"));
const UserDetails = lazy(() => import("./user-pages/user-management/UserDetails"));
const Transaction = lazy(() => import("./user-pages/transaction/Transaction"));
const OrderDetails = lazy(() => import("./user-pages/order-details/OrderDetails"));
const Login = lazy(() => import("./auth-pages/Login"));
const Register = lazy(() => import("./auth-pages/Registration"));
const ForgotPassword = lazy(() => import("./auth-pages/ForgotPassword"));
const TelegramBot = lazy(() => import('./user-pages/telegram-bot/TelegramBot'));
const LabelManagement = lazy(() => import('./user-pages/label-management/LabelManagement'));
const AppRoutes = () => {
	const location = useLocation();
	const { profileData } = useProfileContext() || {};
	const noLayoutRoutes = ["login", "register", "forgot-password"];
	const isNoLayout = noLayoutRoutes.includes(location.pathname);

	return (
		<Suspense fallback={<Spinner />}>
			<Routes>
				<Route path="*" element={<Navigate to="/login" />} />
				<Route path="/login" element={<Login />} />
				{IS_ALGODELTAFX_DOMAIN && (
					<Route path="/register" element={<Register />} />
				)}
				<Route path="/forgot-password" element={<ForgotPassword />} />
				{/* Protected / Authenticated routes */}
				{!isNoLayout && (
					<>
						<Route path="/dashboard" element={<Dashboard />} />
						<Route path="/accounts" element={<AccountManagement />} />
						<Route path="/accounts/:broker_id" element={<AccountInfo />} />
						<Route path="/accounts/symbol/:broker_id" element={<AccountSymbolInfo />} />
						{/* Conditional Routes based on Whitelabel status */}
						{profileData?.is_whitelabel ? (
							<>
								<Route path="/copytrading" element={<GroupStrategy />} />
								<Route path="/copytrading/manage/:group_strategy_id" element={<GroupStrategyDetails />} />
							</>
						) : (
							<>
								<Route path="/copytrading" element={<Group />} />
							</>
						)}
						<Route path="/copytrading/:group_id" element={<GroupInfo />} />
						<Route path="/copytrading/:group_id/account/:broker_id" element={<AccountInfo />} />
						<Route path="/copytrading/:group_id/positions" element={<GroupPositions />} />
						<Route path="/copytrading/:group_id/pendingorders" element={<GroupPendingOrders />} />
						<Route path="/copytrading/:group_id/pendingorderusers/:order_id" element={<GroupPendingOrderDetails />} />
						<Route path="/bridge" element={<Bridge />} />
						<Route path="/bridge/:bridge_id" element={<BridgeInfo />} />
						<Route path="/bridge/tradingviewbridge/:bridge_id" element={<TradingViewBridgeSyntaxGenerator />} />
						<Route path="/orderhistory" element={<OrderHistory />} />
						<Route path="/orderhistory/group/:order_id" element={<GroupOrderHistoryDetails />} />
						<Route path="/wallet" element={<Wallet />} />
						<Route path="/referral" element={<Referral />} />
						<Route path="/activitylogs" element={<ActivityLogs />} />
						<Route path="/profile" element={<Profile />} />
						<Route path="/orderdetails" element={<OrderDetails />} />
						{profileData?.telegram_feature_enabled && (
							<Route path="/telegram-bot" element={<TelegramBot />} />
						)}
						{/* Whitelabel specific routes */}
						{profileData?.is_whitelabel && (
							<>
								<Route path="/user-management" element={<UserManagement />} />
								<Route path="/user-management/:sub_user_id" element={<UserDetails />} />
								<Route path="/user-management/account/:broker_id" element={<AccountInfo />} />
								<Route path="/transaction" element={<Transaction />} />
								<Route path="/configs" element={<Configs />} />
								<Route path="/label-management" element={<LabelManagement />} />
							</>
						)}
					</>
				)}
			</Routes>
		</Suspense>
	);
};

export default AppRoutes;
